import React, { useEffect, useState } from "react";
import { FaChartLine, FaChevronDown } from "react-icons/fa";
import { getAllStudents, getUserById } from "../../../services/operations/authAPI";
import { useDispatch, useSelector } from "react-redux";
import AIAnalysisContent from "../../../components/Dashboard/Parent/AIAnalysisContent";
import AIAnalysisNavigation from "../../../components/Dashboard/Parent/AIAnalysisNavigation";
import { useTranslation } from "react-i18next";

export default function StudentProgress() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(t("studentProgress.selectStudent"));
  const [selectedStudentParents, setSelectedStudentParents] = useState();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [activeAISection, setActiveAISection] = useState("overview");
  const { user } = useSelector((state) => state.profile);
  const [aiReport, setAiReport] = useState(user?.ai_insight_report || null);

  const [linkedUser, setLinkedUser] = useState();

  // NEW STATES FOR CLASS FILTER
  const [selectedClass, setSelectedClass] = useState("All");
  const [classDropdownOpen, setClassDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const data = await dispatch(getAllStudents());

        if (data.success && data.students) {
          const formattedStudents = data.students.map((s) => ({
            name: s.name,
            class: s.class, // ⬅️ NEW FIELD
            parents: s.parents
              ? s.parents.map((p) => ({ id: p._id, name: p.name }))
              : [],
          }));

          setStudents(formattedStudents);
        }
      } catch (error) {
        console.error("Failed to fetch students:", error);
      }
    };

    fetchStudents();
  }, [dispatch]);

  // Fetch parent details
  useEffect(() => {
    const fetchLinkedUser = async () => {
      if (selectedStudentParents) {
        const User = await dispatch(getUserById(selectedStudentParents));
        setLinkedUser(User);
      }
    };
    fetchLinkedUser();
  }, [selectedStudentParents, dispatch]);

  useEffect(() => {
    if (!linkedUser) {
      setAiReport([]);
      return;
    }

    if (i18n.language === "te") {
      setAiReport(linkedUser.ai_insights_telugu || []);
    } else {
      setAiReport(linkedUser.ai_insight_report || []);
    }
  }, [linkedUser, i18n.language]);

  // CLASS LIST
  const classes = ["All", ...new Set(students.map((s) => s.class))];

  // FILTERED STUDENT LIST
  const filteredStudents =
    selectedClass === "All"
      ? students
      : students.filter((s) => s.class === selectedClass);

  const handleSelect = (student) => {
    setSelectedStudent(student.name);

    if (student.parents.length > 0) {
      setSelectedStudentParents(student.parents[0]?.id);
    } else {
      setSelectedStudentParents(null);
      setLinkedUser(null);
    }

    setDropdownOpen(false);
  };

  return (
    <main className="text-[#1B1F3B] min-h-screen p-6">
      <div className="max-w-full mx-auto">

        {/* HEADER */}
        <header className="flex justify-between items-start mb-4">
          <div className="flex items-center space-x-4">
            <div className="bg-[#E0E7FF] rounded-2xl p-3">
              <FaChartLine className="w-8 h-8 text-[#6366F1]" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold leading-tight">
                {t("studentProgress.title")}
              </h1>
              <p className="text-sm text-[#4B5563]">
                {t("studentProgress.subtitle")}s
              </p>
            </div>
          </div>

          {/* RIGHT SIDE FILTERS */}
          <div className="flex gap-4">

            {/* CLASS FILTER DROPDOWN */}
            <div className="relative">
              <button
                onClick={() => setClassDropdownOpen(!classDropdownOpen)}
                className="flex items-center justify-between gap-2 bg-white border border-[#E5E7EB] rounded-lg px-4 py-2 text-sm font-medium text-[#374151] shadow-sm hover:bg-[#F9FAFB]"
              >
                {selectedClass === "All" ? t("studentProgress.allclasses") : `Class ${selectedClass}`}
                <FaChevronDown className="text-[#9CA3AF] w-4 h-4" />
              </button>

              {classDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                  {classes.map((cls, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        setSelectedClass(cls);
                        setClassDropdownOpen(false);
                      }}
                      className="px-4 py-2 text-sm text-[#374151] hover:bg-[#F3F4F6] cursor-pointer"
                    >
                      {cls === "All" ? t("studentProgress.allclasses") : `Class ${cls}`}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* STUDENT DROPDOWN */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center justify-between gap-2 bg-white border border-[#E5E7EB] rounded-lg px-4 py-2 text-sm font-medium text-[#374151] shadow-sm hover:bg-[#F9FAFB]"
              >
                {selectedStudent}
                <FaChevronDown className="text-[#9CA3AF] w-4 h-4" />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map((student, idx) => (
                      <div
                        key={idx}
                        onClick={() => handleSelect(student)}
                        className="px-4 py-2 text-sm text-[#374151] hover:bg-[#F3F4F6] cursor-pointer"
                      >
                        <div className="font-medium">
                          {student.name} <span className="text-xs text-gray-500">(Class {student.class})</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-[#9CA3AF]">
                      {t("studentProgress.loading")}
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </header>

        {/* AI ANALYSIS */}
        <div className="mt-6">
          <AIAnalysisNavigation
            activeSection={activeAISection}
            setActiveSection={setActiveAISection}
            report={aiReport}
          />
          <AIAnalysisContent
            activeSection={activeAISection}
            report={aiReport}
            linkedUser={linkedUser}
          />
        </div>
      </div>
    </main>
  );
}
